import { Edit2, KeyRound, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { getUserAriaSort, getUserSortIcon } from '../../../utils/user.helpers';
import { UserSortKey } from '../../../types';
import { UsersTableProps } from './userTypes';

export function UsersTable({
  users,
  usersLoading,
  roleCatalog,
  currentUserId,
  usersSort,
  toggleUsersSort,
  onEditRoles,
  onResetPassword,
  onDelete
}: UsersTableProps) {
  const renderSortableHeader = (
    column: UserSortKey,
    label: string,
    className?: string
  ) => (
    <TableHead
      aria-sort={getUserAriaSort(column, usersSort.column, usersSort.direction)}
      className={className}
    >
      <button
        type="button"
        onClick={() => toggleUsersSort(column)}
        className="flex w-full items-center gap-1 bg-transparent text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        {label}
        {getUserSortIcon(usersSort.column === column, usersSort.direction)}
      </button>
    </TableHead>
  );

  return (
    <div className="hidden md:block overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {renderSortableHeader('name', 'Nome')}
            <TableHead>Username</TableHead>
            {renderSortableHeader('email', 'E-mail')}
            {renderSortableHeader('roles', 'Papeis')}
            {renderSortableHeader('status', 'Status')}
            <TableHead
              className="hidden text-right md:table-cell"
              aria-sort={getUserAriaSort('createdAt', usersSort.column, usersSort.direction)}
            >
              <button
                type="button"
                onClick={() => toggleUsersSort('createdAt')}
                className="ml-auto flex w-full items-center justify-end gap-1 bg-transparent text-right focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                Criado em
                {getUserSortIcon(usersSort.column === 'createdAt', usersSort.direction)}
              </button>
            </TableHead>
            <TableHead className="text-right">Acoes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {usersLoading ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-sm">
                Carregando usuarios...
              </TableCell>
            </TableRow>
          ) : users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-sm">
                Nenhum usuario encontrado.
              </TableCell>
            </TableRow>
          ) : (
            users.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.name}</TableCell>
                <TableCell className="text-muted-foreground">{user.username}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {user.roles.map((role) => {
                      const roleInfo = roleCatalog.find((r) => r.id === role);
                      return (
                        <Badge key={role} variant="secondary" className="text-xs">
                          {roleInfo?.label || role}
                        </Badge>
                      );
                    })}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={user.isActive ? 'secondary' : 'destructive'}>
                    {user.isActive ? 'Ativo' : 'Inativo'}
                  </Badge>
                </TableCell>
                <TableCell className="hidden text-right md:table-cell text-xs text-muted-foreground">
                  {new Date(user.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditRoles(user)}
                      title="Editar papeis"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onResetPassword(user)}
                      title="Redefinir senha"
                    >
                      <KeyRound className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(user)}
                      disabled={user.id === currentUserId}
                      title={user.id === currentUserId ? 'Nao pode deletar seu proprio usuario' : 'Deletar usuario'}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
